// Per-batch log of the (listing, platform) creates the Chrome extension has already finished, so
// reloading the page part-way through a run does not repeat work that is already done.
//
// This is a convenience only — the server-side claim is the durable guard against creating the
// same marketplace post twice. Each run writes only its own batch's log, so a run that outlives
// the batch on screen can never make a later batch skip work it has not actually done.

const PROGRESS_PREFIX = 'bulk_cross_list_ext_progress:';
const PROGRESS_VERSION = 1;
const PROGRESS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const PROGRESS_MAX_COUNT = 5;

function progressKey(batchId) {
  return `${PROGRESS_PREFIX}${batchId}`;
}

/**
 * Everything already finished for one batch.
 * @param {string} batchId
 * @returns {Array<{listingId:string, platform:string, result:object}>}
 */
export function readCompletedWork(batchId) {
  if (!batchId) return [];
  try {
    const raw = localStorage.getItem(progressKey(batchId));
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== PROGRESS_VERSION || !Array.isArray(parsed.completed)) {
      return [];
    }
    return parsed.completed;
  } catch (e) {
    return [];
  }
}

/**
 * Adds one finished (listing, platform) to a batch's log, replacing any earlier outcome for that
 * same pair so a retry overwrites the failure it replaces.
 */
export function recordCompletedWork(batchId, listingId, platform, result) {
  if (!batchId) return;
  try {
    const completed = readCompletedWork(batchId)
      .filter((entry) => !(entry.listingId === listingId && entry.platform === platform))
      .concat({ listingId, platform, result });

    localStorage.setItem(progressKey(batchId), JSON.stringify({
      completed,
      version: PROGRESS_VERSION,
      updatedAt: new Date().toISOString(),
    }));
  } catch (e) {
    // Storage can be full or blocked; the run continues either way.
  }
}

// Keeps the logs from growing forever: drops anything older than 7 days, then keeps only the 5
// most recently written. Only ever looks at — and only ever removes — keys under this module's
// own prefix. A log with no readable timestamp is treated as ancient, so a corrupted entry
// cleans itself up instead of staying forever.
export function pruneExtensionProgress() {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PROGRESS_PREFIX)) keys.push(key);
    }

    const now = Date.now();
    const survivors = [];
    keys.forEach((key) => {
      let updatedAt = 0;
      try {
        updatedAt = Date.parse(JSON.parse(localStorage.getItem(key))?.updatedAt) || 0;
      } catch (e) {
        updatedAt = 0;
      }

      if (now - updatedAt > PROGRESS_MAX_AGE_MS) localStorage.removeItem(key);
      else survivors.push({ key, updatedAt });
    });

    survivors
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(PROGRESS_MAX_COUNT)
      .forEach(({ key }) => localStorage.removeItem(key));
  } catch (e) {
    // Best-effort cleanup — a broken storage just means the logs stay as they are.
  }
}
